import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { AppButton } from "../ui";

/**
 * ImageLightbox — the question's figure at full size.
 *
 * Circuit diagrams, code screenshots and tree figures are routinely authored
 * larger than the card gives them, and a candidate who cannot read the figure
 * cannot answer the question.
 *
 * It swallows keystrokes in the CAPTURE phase while open. The runner's own
 * keyboard shortcuts live on a bubble-phase window listener, so without this
 * an arrow key pressed while looking at the picture would silently move the
 * candidate to the next question behind it.
 */
export default function ImageLightbox({ src, alt = "", onClose }) {
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    const restore = document.activeElement;
    closeRef.current?.focus();

    const onKey = (e) => {
      e.stopPropagation();
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current?.();
      }
    };

    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      restore?.focus?.();
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Question image"
      onClick={onClose}
      className="tw-fixed tw-inset-0 tw-z-modal tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-bg-surface-dark/90 tw-p-4 sm:tw-p-8 tw-animate-panel-fade"
    >
      <AppButton
        ref={closeRef}
        type="button"
        variant="secondary"
        size="sm"
        onClick={onClose}
        className="tw-self-end"
      >
        <X size={16} aria-hidden="true" /> Close
      </AppButton>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="tw-min-h-0 tw-max-w-full tw-flex-1 tw-rounded-sm tw-bg-surface-raised tw-object-contain"
      />
    </div>
  );
}
